<template>
  <div class="route-admin">

    <!-- ============ 목록 화면 ============ -->
    <div v-if="mode === 'list'">
      <div class="route-admin__toolbar">
        <h2 class="route-admin__h2">버스운행루트 관리</h2>
        <button type="button" class="btn btn--primary" @click="openNewRoute">+ 새 루트</button>
      </div>

      <table class="route-admin__table">
        <thead>
          <tr>
            <th>루트명</th>
            <th>여행지</th>
            <th>레이어</th>
            <th>사용여부</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in routes" :key="r.routeId">
            <td>{{ r.routeName }}</td>
            <td>{{ r.destination || '-' }}</td>
            <td>{{ r.layoutName || '-' }}</td>
            <td>
              <span :class="['badge', r.useFlag === 'Y' ? 'badge--on' : 'badge--off']">
                {{ r.useFlag === 'Y' ? '사용중' : '중지' }}
              </span>
            </td>
            <td class="route-admin__actions">
              <button type="button" class="btn btn--sm" @click="openEditRoute(r.routeId)">수정</button>
              <button type="button" class="btn btn--sm" @click="duplicateRoute(r.routeId)">복제</button>
              <button type="button" class="btn btn--sm" @click="toggleUseFlag(r)">
                {{ r.useFlag === 'Y' ? '사용중지' : '사용재개' }}
              </button>
              <button type="button" class="btn btn--sm btn--danger" @click="deleteRoute(r)">삭제</button>
            </td>
          </tr>
          <tr v-if="routes.length === 0">
            <td colspan="5" class="route-admin__empty">등록된 루트가 없습니다.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ============ 에디터 화면 ============ -->
    <div v-else>
      <div class="route-admin__toolbar">
        <h2 class="route-admin__h2">{{ editing.routeId ? '루트 수정' : '새 루트' }}</h2>
        <div>
          <button type="button" class="btn" @click="mode = 'list'">목록으로</button>
          <button type="button" class="btn btn--primary" :disabled="saving" @click="save">
            {{ saving ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>

      <!-- 기본정보 -->
      <section class="route-admin__section">
        <h3 class="route-admin__h3">기본정보</h3>
        <div class="route-admin__field-row">
          <div class="route-admin__field">
            <label>루트명</label>
            <input v-model="editing.routeName" type="text" placeholder="예: 경주당일투어 A코스" />
          </div>
          <div class="route-admin__field">
            <label>여행지</label>
            <input v-model="editing.destination" type="text" placeholder="예: 경주" />
          </div>
        </div>
        <div class="route-admin__field-row">
          <div class="route-admin__field">
            <label>좌석 레이어</label>
            <select v-model.number="editing.layoutId">
              <option :value="null">선택 안함</option>
              <option v-for="l in layouts" :key="l.layoutId" :value="l.layoutId">{{ l.layoutCode }} · {{ l.layoutName }}</option>
            </select>
          </div>
          <!-- 가이드는 호차마다 다르므로 운행 화면(Ma_BusRoundAdmin)에서 호차별로 입력. guideId 는 기존 값 그대로 저장 -->
        </div>
      </section>

      <!-- 집결지 (구글맵) -->
      <section class="route-admin__section">
        <h3 class="route-admin__h3">집결지 <span class="route-admin__required">필수</span></h3>
        <div class="route-admin__field-row">
          <div class="route-admin__field">
            <label>집결지명</label>
            <input v-model="editing.meetingPointName" type="text" placeholder="예: 부산역 광장" />
          </div>
          <div class="route-admin__field route-admin__field--grow">
            <label>주소 검색</label>
            <input ref="meetingAddressInput" v-model="editing.meetingAddress" type="text" placeholder="주소를 입력하면 자동완성됩니다" />
          </div>
        </div>
        <div id="route-admin-map" class="route-admin__map"></div>
        <p class="route-admin__hint">지도의 마커를 드래그하여 위치를 미세조정할 수 있습니다.</p>
      </section>

      <!-- 이동포인트 -->
      <section class="route-admin__section">
        <h3 class="route-admin__h3">이동포인트 (경유지)</h3>
        <table class="route-admin__table route-admin__table--compact">
          <thead>
            <tr>
              <th>순서</th>
              <th>이름</th>
              <th>주소</th>
              <th>이동시간(분)</th>
              <th>체류시간(분)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, idx) in editing.points" :key="idx">
              <td>{{ idx + 1 }}</td>
              <td><input v-model="p.pointName" type="text" placeholder="경유지명" /></td>
              <td><input v-model="p.address" type="text" placeholder="주소" /></td>
              <td><input v-model.number="p.moveTime" type="number" min="0" style="width:60px" /></td>
              <td><input v-model.number="p.stayTime" type="number" min="0" style="width:60px" /></td>
              <td>
                <button type="button" class="btn btn--sm" :disabled="idx === 0" @click="movePoint(idx, -1)">↑</button>
                <button type="button" class="btn btn--sm" :disabled="idx === editing.points.length - 1" @click="movePoint(idx, 1)">↓</button>
                <button type="button" class="btn btn--sm btn--danger" @click="removePoint(idx)">삭제</button>
              </td>
            </tr>
          </tbody>
        </table>
        <button type="button" class="btn btn--sm" @click="addPoint">+ 경유지 추가</button>
      </section>

      <!-- 상품매칭 -->
      <section class="route-admin__section">
        <h3 class="route-admin__h3">상품 매칭 (채널별)</h3>
        <p class="route-admin__hint route-admin__hint--top">
          좌석지정은 <b>홈페이지(HOME)</b> 채널로 매칭된 상품의 예약만 대상입니다. 홈페이지 상품은 반드시 HOME으로 선택해 주십시오.
        </p>
        <table class="route-admin__table route-admin__table--compact">
          <thead>
            <tr>
              <th>채널</th>
              <th>상품코드</th>
              <th>상품명 (부가정보)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, idx) in editing.products" :key="idx">
              <td>
                <select v-model="p.channel" class="route-admin__channel">
                  <option value="">선택</option>
                  <option v-for="ch in channelOptions" :key="ch.value" :value="ch.value">{{ ch.label }}</option>
                </select>
              </td>
              <td><input v-model="p.productCode" type="text" placeholder="상품코드" /></td>
              <td><input v-model="p.productName" type="text" placeholder="상품명" /></td>
              <td><button type="button" class="btn btn--sm btn--danger" @click="removeProduct(idx)">삭제</button></td>
            </tr>
          </tbody>
        </table>
        <button type="button" class="btn btn--sm" @click="addProduct">+ 상품 추가</button>
      </section>

    </div>
  </div>
</template>

<script>
export default {
  name: 'Ma_BusRouteAdmin',

  data() {
    return {
      mode: 'list', // 'list' | 'edit'
      routes: [],
      layouts: [],
      editing: this.blankRoute(),
      saving: false,
      map: null,
      marker: null,
      autocomplete: null,
      // 판매채널 코드 - 좌석지정 프로시저는 HOME 채널 상품만 자사 예약으로 매칭해요
      channelOptions: [
        { value: 'HOME', label: '홈페이지' },
        { value: 'NAVER', label: '네이버' },
        { value: 'OTHER', label: '기타 판매처' },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
  },

  created() {
    this.user = this.$store.state.user;
    this.loadRoutes();
    this.loadLayouts();
  },

  mounted() {
    // 권한없는 로그인 차단
    if (this.user.handlerGrade >= 20 || this.user.handlerGrade == undefined) {
      var swal = require('sweetalert2');
      swal.fire({
        title: '로그인 필요',
        text: '접근권한 확인불가.',
        icon: 'error'
      });
      this.$router.push({ path: '/' });
    }

    if (this.user.handlerId != undefined) {
      document.getElementById("admin").style.display = "block";
    }
    document.getElementById("user").style.display = "none";
  },

  methods: {
    blankRoute() {
      return {
        routeId: null,
        routeName: '',
        destination: '',
        layoutId: null,
        guideId: '',
        meetingPointName: '',
        meetingAddress: '',
        meetingLat: null,
        meetingLng: null,
        points: [],
        products: [],
      };
    },

    async loadRoutes() {
      const res = await this.$api('/api/getBusRouteList', { param: [] });
      if (Array.isArray(res)) this.routes = res;
    },

    async loadLayouts() {
      const res = await this.$api('/api/getBusLayoutList', { param: [] });
      if (Array.isArray(res)) this.layouts = res.filter((l) => l.useFlag === 'Y');
    },

    openNewRoute() {
      this.editing = this.blankRoute();
      this.mode = 'edit';
      this.$nextTick(() => this.loadGoogleMaps());
    },

    // 루트 1건의 상세 + 이동포인트 + 상품매칭을 한 번에 불러옴 (수정/복제 공용)
    // 목록(getBusRouteList)에는 집결지좌표/layoutId가 없어서 상세조회로 다시 불러옴
    async fetchRoute(routeId) {
      const [detailRes, pointsRes, productsRes] = await Promise.all([
        this.$api('/api/getBusRouteDetail', { param: [routeId] }),
        this.$api('/api/getBusRoutePoints', { param: [routeId] }),
        this.$api('/api/getBusRouteProducts', { param: [routeId] }),
      ]);
      const detail = Array.isArray(detailRes) ? detailRes[0] : null;
      if (!detail) return null;

      return {
        ...this.blankRoute(),
        ...detail,
        points: Array.isArray(pointsRes) ? pointsRes : [],
        products: Array.isArray(productsRes) ? productsRes : [],
      };
    },

    async openEditRoute(routeId) {
      const route = await this.fetchRoute(routeId);
      if (!route) {
        alert('루트 정보를 불러오지 못했습니다.');
        return;
      }
      this.editing = route;
      this.mode = 'edit';
      this.$nextTick(() => this.loadGoogleMaps());
    },

    // 루트복제 — 기존 루트를 불러와 routeId만 비운 "새 루트" 상태로 편집기를 엽니다.
    // 이 시점엔 DB에 아무것도 저장되지 않고, 내용을 확인/수정한 뒤 [저장]을 눌러야
    // 신규 루트로 등록돼요 (기존 saveBusRoute가 routeId=null이면 INSERT 처리).
    async duplicateRoute(routeId) {
      const route = await this.fetchRoute(routeId);
      if (!route) {
        alert('복제할 루트 정보를 불러오지 못했습니다.');
        return;
      }
      route.routeId = null;
      route.routeName = `${route.routeName}`.slice(0, 43) + ' (복사)'; // routeName은 VARCHAR(50)
      this.editing = route;
      this.mode = 'edit';
      this.$nextTick(() => this.loadGoogleMaps());
    },

    addPoint() {
      // spotSeq: 관광지(tb_area_spot) 연결값 - 화면 입력은 없고, 불러온 값은 저장할 때 그대로 유지
      this.editing.points.push({ pointName: '', address: '', moveTime: null, stayTime: null, spotSeq: null });
    },
    removePoint(idx) {
      this.editing.points.splice(idx, 1);
    },
    movePoint(idx, dir) {
      const target = idx + dir;
      if (target < 0 || target >= this.editing.points.length) return;
      const points = this.editing.points;
      [points[idx], points[target]] = [points[target], points[idx]];
    },

    addProduct() {
      this.editing.products.push({ channel: 'HOME', productCode: '', productName: '' });
    },
    removeProduct(idx) {
      this.editing.products.splice(idx, 1);
    },

    async toggleUseFlag(route) {
      const nextFlag = route.useFlag === 'Y' ? 'N' : 'Y';
      const res = await this.$api('/api/toggleBusRouteUseFlag', { param: [nextFlag, route.routeId] });
      if (res && res.affectedRows === 1) {
        route.useFlag = nextFlag;
      }
    },

    // 루트삭제 — 소프트삭제(viewFlag='N'). 데이터는 DB에 남고 목록에서만 사라져요.
    // 이미 만들어진 회차/예약의 루트 정보에는 영향이 없어요.
    async deleteRoute(route) {
      const swal = require('sweetalert2');
      const ask = await swal.fire({
        title: '루트 삭제',
        text: `'${route.routeName}' 루트를 목록에서 삭제하시겠습니까? 이미 만들어진 회차와 예약에는 영향이 없습니다.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '삭제',
        cancelButtonText: '취소',
      });
      if (!ask.isConfirmed) return;

      const res = await this.$api('/api/deleteBusRoute', { param: [this.user.userId, route.routeId] });
      if (res && res.affectedRows === 1) {
        await this.loadRoutes();
      } else {
        alert('삭제에 실패했습니다: ' + (res && res.error ? res.error : '알 수 없는 오류'));
      }
    },

    // ---- 구글맵 ----
    loadGoogleMaps() {
      if (window.google?.maps) {
        this.initMap();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_API_KEY}&language=ko&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => this.initMap();
      document.head.appendChild(script);
    },

    initMap() {
      const hasCoord = this.editing.meetingLat && this.editing.meetingLng;
      const center = hasCoord
        ? { lat: Number(this.editing.meetingLat), lng: Number(this.editing.meetingLng) }
        : { lat: 35.1796, lng: 129.0756 }; // 기본값: 부산

      this.map = new window.google.maps.Map(document.getElementById('route-admin-map'), {
        center,
        zoom: hasCoord ? 16 : 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      this.marker = new window.google.maps.Marker({
        position: center,
        map: this.map,
        draggable: true,
      });

      this.marker.addListener('dragend', () => {
        const pos = this.marker.getPosition();
        this.editing.meetingLat = pos.lat();
        this.editing.meetingLng = pos.lng();
      });

      // 주소 자동완성
      this.autocomplete = new window.google.maps.places.Autocomplete(this.$refs.meetingAddressInput, {
        fields: ['formatted_address', 'geometry', 'name'],
      });
      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        if (!place.geometry) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        this.editing.meetingAddress = place.formatted_address || this.editing.meetingAddress;
        this.editing.meetingLat = lat;
        this.editing.meetingLng = lng;
        if (!this.editing.meetingPointName) this.editing.meetingPointName = place.name || '';

        this.map.setCenter({ lat, lng });
        this.map.setZoom(16);
        this.marker.setPosition({ lat, lng });
      });
    },

    async save() {
      if (!this.editing.routeName) {
        alert('루트명을 입력해 주십시오.');
        return;
      }
      if (!this.editing.meetingPointName || this.editing.meetingLat == null) {
        alert('집결지 정보(이름, 위치)를 입력해 주십시오.');
        return;
      }
      const badProduct = this.editing.products.find((p) => !p.channel || !String(p.productCode || '').trim());
      if (badProduct) {
        alert('상품 매칭의 채널과 상품코드를 모두 입력해 주십시오.');
        return;
      }

      this.saving = true;
      try {
        const res = await this.$api('/api/proc_saveBusRoute', {
          param: [
            this.editing.routeId,
            this.editing.routeName,
            this.editing.destination,
            this.editing.layoutId,
            this.editing.guideId,
            this.editing.meetingPointName,
            this.editing.meetingAddress,
            this.editing.meetingLat,
            this.editing.meetingLng,
            JSON.stringify(this.editing.points),
            JSON.stringify(this.editing.products),
            this.user.userId,
          ],
        });

        // proc_saveBusRoute는 마지막에 SELECT v_routeId AS routeId 를 리턴해요.
        // CALL 응답은 드라이버에 따라 [{routeId}] 또는 [[{routeId}], OkPacket] 형태라서 둘 다 처리합니다.
        const first = Array.isArray(res) ? res[0] : null;
        const row = Array.isArray(first) ? first[0] : first;
        if (row && row.routeId) {
          await this.loadRoutes();
          this.mode = 'list';
        } else {
          console.log('saveBusRoute 응답:', res);
          alert('저장에 실패했습니다: ' + (res && res.error ? res.error : '알 수 없는 오류\n(응답: ' + JSON.stringify(res) + ')'));
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.route-admin {
  padding: 20px;
  width: 80%;
  margin: 0 auto;
}

.route-admin__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.route-admin__h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}
.route-admin__h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
}
.route-admin__required {
  font-size: 11px;
  color: #d64545;
  font-weight: 400;
  margin-left: 6px;
}

.route-admin__section {
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.route-admin__field-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}
.route-admin__field {
  flex: 1;
}
.route-admin__field--grow {
  flex: 2;
}
.route-admin__field label {
  display: block;
  font-size: 12px;
  color: #777;
  margin-bottom: 4px;
}
.route-admin__field input,
.route-admin__field select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;
}

.route-admin__map {
  width: 100%;
  height: 260px;
  border-radius: 8px;
  border: 1px solid #e2e2e2;
}
.route-admin__hint {
  font-size: 11px;
  color: #999;
  margin: 8px 0 0;
}
.route-admin__hint--top {
  margin: 0 0 10px;
}
.route-admin__channel {
  padding: 4px 8px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 12px;
}

.route-admin__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.route-admin__table th,
.route-admin__table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e2e2;
  text-align: left;
}
.route-admin__table--compact input {
  padding: 4px 8px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 12px;
  width: 100%;
  box-sizing: border-box;
}
.route-admin__actions {
  display: flex;
  gap: 6px;
}
.route-admin__empty {
  text-align: center;
  color: #999;
  padding: 24px;
}

.badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
}
.badge--on {
  background: #e1f5ee;
  color: #0f6e56;
}
.badge--off {
  background: #f1efe8;
  color: #5f5e5a;
}

.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}
.btn--primary {
  background: #2f7d5f;
  border-color: #2f7d5f;
  color: #fff;
}
.btn--primary:disabled {
  background: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
}
.btn--sm {
  padding: 4px 10px;
  font-size: 12px;
}
.btn--danger {
  color: #d64545;
  border-color: #f0c6c6;
}
</style>